/* eslint-disable no-plusplus */
/* eslint-disable camelcase */
import { ContractType, Product } from 'types/IProducts';

import {
  Payload,
  SIDE,
  calcPrecision,
  convertExponentialToDecimal,
  getOverlayPopoverCalculation,
  roundByPrecision,
  roundPriceLevel,
} from './utils';

interface ComputeOrderbookInterface {
  book: Array<[string, number, number]>;
  selectedPriceClubbingValue: number;
  side: string;
  hideDepth: boolean;
  selectedProduct: Product;
  symbol: string;
}

const toNumber = val => Number(val); // todo - compare with unary operator later

const calcBookWithDepth = (sortedBook, totalDepth, selectedProduct, symbol, side) => {
  const bookWithDepth: Required<Payload>[] = [];
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  // ? for loop is faster than array methods
  for (let i = 0; i < sortedBook.length; i++) {
    const order = sortedBook[i];
    const depth = (bookWithDepth[i - 1]?.depth || 0) + order.size;

    const obj: any = {
      price: convertExponentialToDecimal(order.price),
      size: order.size,
      depth,
      depthPercentage: (depth / totalDepth) * 100,
      symbol,
      // Initialize overlay properties with null values
      average: null,
      underlyingNotionalSum: null,
      settlingNotionalSum: null,
      quotingNotionalSum: null,
    };

    // Only calculate overlay data for desktop
    if (isDesktop) {
      const { average, underlyingNotionalSum, settlingNotionalSum, quotingNotionalSum } =
        getOverlayPopoverCalculation(sortedBook.slice(0, i + 1), selectedProduct)();

      obj.average = average;
      obj.underlyingNotionalSum = underlyingNotionalSum;
      obj.settlingNotionalSum = settlingNotionalSum;
      obj.quotingNotionalSum = quotingNotionalSum;
    }
    bookWithDepth.push(obj);
  }
  if (side === SIDE.SELL) {
    bookWithDepth.reverse();
  }

  return bookWithDepth;
};

const computeOrderbook = ({
  book = [],
  selectedPriceClubbingValue,
  side,
  hideDepth,
  selectedProduct,
  symbol,
}: ComputeOrderbookInterface) => {
  const tickSize = selectedProduct?.tick_size;
  const precision = calcPrecision(tickSize);
  const computedBookMap = new Map();
  let totalDepth = 0;

  // ? for loop is faster than array methods
  for (let i = 0; i < book.length; i++) {
    const [limit_price, size] = book[i];
    const price = parseFloat(limit_price);
    const clubbedPrice = toNumber(
      roundByPrecision(precision)(
        roundPriceLevel(
          toNumber(price),
          toNumber(convertExponentialToDecimal(selectedPriceClubbingValue)),
          side,
          toNumber(tickSize)
        )
      )
    );

    const clubbedSize = toNumber(size);
    if (computedBookMap.has(clubbedPrice)) {
      const existingSize = computedBookMap.get(clubbedPrice);
      computedBookMap.set(clubbedPrice, existingSize + clubbedSize);
    } else {
      computedBookMap.set(clubbedPrice, clubbedSize);
    }

    totalDepth += toNumber(size);
  }

  const entries = Array.from(computedBookMap.entries(), ([price, size]) => ({
    price,
    size,
  }));
  const compareFn =
    side === SIDE.SELL
      ? (a, b) => toNumber(a.price) - toNumber(b.price)
      : (a, b) => toNumber(b.price) - toNumber(a.price);

  const sortedBook = entries.sort(compareFn);

  if (hideDepth) {
    return sortedBook;
  }

  return calcBookWithDepth(sortedBook, totalDepth, selectedProduct, symbol, side);
};

export const getComputedOrderbookFromSocket = (
  message: {
    a: [string, number, number][];
    b: [string, number, number][];
    i: number;
    lts: number;
    pts: number;
    s: string;
    type: string;
    d: string;
    pd: string;
    error?: boolean;
  },
  state$
) => {
  const selectedProduct = state$.value?.trade?.selected_product;
  const selectedPriceClubbingValue =
    state$.value?.l2Orderbook?.selectedPriceClubbingValue;
  const contractType = state$?.value?.trade?.selectedContractType;
  const hideDepth =
    contractType === ContractType.CallOptions || contractType === ContractType.PutOptions;

  const buy = computeOrderbook({
    book: message.b,
    selectedPriceClubbingValue,
    side: SIDE.BUY,
    hideDepth,
    selectedProduct,
    symbol: message.s,
  });

  const sell = computeOrderbook({
    book: message.a,
    selectedPriceClubbingValue,
    side: SIDE.SELL,
    hideDepth,
    selectedProduct,
    symbol: message.s,
  });

  return {
    buy,
    sell,
    product_id: message?.i,
    type: message?.type,
    symbol: message?.s,
    last_sequence_no: message?.lts,
    // last_updated_at: message?.last_updated_at,
    // timestamp: message?.timestamp,
    error: message?.error,
    bestBid: message?.b?.[0]?.[0],
    bestAsk: message?.a?.[0]?.[0],
    bestBidSize: message?.b?.[0]?.[1],
    bestAskSize: message?.a?.[0]?.[1],
    spread: message?.d,
    percentage_spread: message?.pd,
  };
};

export default computeOrderbook;
